/// Port of mobile/lib/theme/app_colors.dart plus the ColorScheme mapping from
/// light_theme.dart and dark_theme.dart.
///
/// The palette is a pure grayscale ladder. `themeColors` is the web stand-in
/// for Flutter's ColorScheme, so call sites read tokens by role rather than
/// reaching for the raw values.

export const appColors = {
  darkSurfaceLowest: '#000000', // rgb(0, 0, 0)
  darkSurfaceLow: '#0a0a0a', // rgb(10, 10, 10)
  darkSurface: '#141414', // rgb(20, 20, 20)
  darkSurfaceHigh: '#1e1e1e', // rgb(30, 30, 30)
  darkSurfaceHighest: '#282828', // rgb(40, 40, 40)
  darkSurfaceDim: '#000000',
  darkSurfaceBright: '#282828',
  darkOutline: '#3c3c3c', // rgb(60, 60, 60)
  darkOutlineVariant: '#303030', // rgb(48, 48, 48) — soft nested borders
  darkOnSurface: '#ffffff',

  lightSurfaceLowest: '#d2d2d2', // rgb(210, 210, 210)
  lightSurfaceLow: '#dcdcdc', // rgb(220, 220, 220)
  lightSurface: '#e6e6e6', // rgb(230, 230, 230)
  lightSurfaceHigh: '#f0f0f0', // rgb(240, 240, 240)
  lightSurfaceHighest: '#fafafa', // rgb(250, 250, 250)
  lightSurfaceDim: '#d2d2d2',
  lightSurfaceBright: '#fafafa',
  lightOutline: '#bebebe', // rgb(190, 190, 190)
  lightOutlineVariant: '#d2d2d2', // rgb(210, 210, 210) — soft nested borders
  lightOnSurface: '#000000',

  /// High-score trophy / score accent — tuned per theme for contrast on game surfaces.
  lightHighScoreGold: '#a67c00',
  darkHighScoreGold: '#ffc107',

  /// Shared accent used for high-score emphasis in the game HUD.
  accent: '#ffc107',
} as const;

export interface ThemeColors {
  surface: string;
  surfaceDim: string;
  surfaceBright: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;
  outline: string;
  outlineVariant: string;
  onSurface: string;
  accent: string;
  highScoreGold: string;
}

export const darkColors: ThemeColors = {
  surface: appColors.darkSurface,
  surfaceDim: appColors.darkSurfaceDim,
  surfaceBright: appColors.darkSurfaceBright,
  surfaceContainerLowest: appColors.darkSurfaceLowest,
  surfaceContainerLow: appColors.darkSurfaceLow,
  surfaceContainer: appColors.darkSurface,
  surfaceContainerHigh: appColors.darkSurfaceHigh,
  surfaceContainerHighest: appColors.darkSurfaceHighest,
  outline: appColors.darkOutline,
  outlineVariant: appColors.darkOutlineVariant,
  onSurface: appColors.darkOnSurface,
  accent: appColors.darkHighScoreGold,
  highScoreGold: appColors.darkHighScoreGold,
};

export const lightColors: ThemeColors = {
  surface: appColors.lightSurface,
  surfaceDim: appColors.lightSurfaceDim,
  surfaceBright: appColors.lightSurfaceBright,
  surfaceContainerLowest: appColors.lightSurfaceLowest,
  surfaceContainerLow: appColors.lightSurfaceLow,
  surfaceContainer: appColors.lightSurface,
  surfaceContainerHigh: appColors.lightSurfaceHigh,
  surfaceContainerHighest: appColors.lightSurfaceHighest,
  outline: appColors.lightOutline,
  outlineVariant: appColors.lightOutlineVariant,
  onSurface: appColors.lightOnSurface,
  accent: appColors.lightHighScoreGold,
  highScoreGold: appColors.lightHighScoreGold,
};

export function colorsFor(isDark: boolean): ThemeColors {
  return isDark ? darkColors : lightColors;
}
